import { Injectable, Injector } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, from, fromEvent, map, Observable, skip, take } from "rxjs";
import { websocketErrorHandleInterceptor } from "src/app/domain/customer-service/interceptors/error-handle-interceptor/websocket-error-handle-interceptor-fn";
import { IUsuarioSocketDefaultResponse, IUsuarioSocketResponse, IWebSocketLogError, TUsuarioSocketResponse } from "src/app/domain/customer-service/models/usuario-socket-models/usuario-socket-models";
import { MessageHandleService } from "src/app/domain/customer-service/service/message-handle/message-handle.service";
import { environment } from "src/environments/environment";
import { UserApiService } from "../../api/user/user.api.service";
import { BsHotToastService } from "../../hot-toast/hot-toast.service";
import { ImageBase64Formater } from "../../utils/image-base64-formater/image-base64-formater";
import { NotificationService } from "../../utils/notification-handle/notification.service";
import { QueryStringGenerator } from "../../utils/query-string-generator/query-string-generator";


@Injectable({
	providedIn: 'root',
})
@UntilDestroy()
export class UsuarioSocketHandleService {
	private readonly API = environment.USUARIO_SOCKET;
	private readonly SOCKET_START_MESSAGE = '{"protocol":"json","version":1}';
	private readonly SOCKET_INITIAL_MESSAGE =
		'{"arguments":[],"invocationId":"0","target":"receivemessage","type":1}';
	readonly CHATBOT_TOKEN_KEY = 'chatToken';

	private socket?: WebSocket;
	private isSocketOpened$ = new BehaviorSubject<boolean>(false);

	constructor(
		private injector: Injector,
		private readonly bsHotToast: BsHotToastService,
		private readonly notificationService: NotificationService,
		private readonly messageHandleService: MessageHandleService,
	) { }

	get socketStatus() {
		return this.isSocketOpened$.value;
	}

	get socketStatus$() {
		return this.isSocketOpened$.asObservable();
	}

	async startDomain(): Promise<boolean> {
		try {
			const socket = this.createSocket();

			const opened = await new Promise<boolean>((resolve, reject) => {
				const onOpen = () => {
					resolve(true);
					socket.removeEventListener('open', onOpen);
				};
				const onError = () => {
					resolve(false);
					socket.removeEventListener('error', onError);
				};

				socket.addEventListener('open', onOpen);
				socket.addEventListener('error', onError);
			});

			if (opened) {
				this.socket = socket;
				this.isSocketOpened$.next(true);
				this.messageHandle(socket);
				return true;
			} else {
				socket.close();
				this.isSocketOpened$.next(false);
				return false;
			}
		} catch {
			this.isSocketOpened$.next(false);
			this.bsHotToast.showError(
				'Ocorreu um erro ao conectar o usuário! Por favor, entre em contato com um administrador.',
			);
			return false;
		}
	}

	disconnectSocket() {
		if (this.socket) {
			this.socket.close();
			this.socket = undefined;
			this.isSocketOpened$.next(false);
		}
	}

	private createSocket() {
		const token = localStorage.getItem(this.CHATBOT_TOKEN_KEY);
		const query = QueryStringGenerator.generateQuery({ access_token: token });
		const socket = new WebSocket(`${this.API}${query}`);

		this.isSocketOpened$.next(true);
		socket.onopen = () => {
			socket.send(this.SOCKET_START_MESSAGE);
		};

		fromEvent(socket, 'message')
			.pipe(take(1))
			.subscribe(() => {
				socket.send(this.SOCKET_INITIAL_MESSAGE);
			});

		this.isSocketOpened$.next(
			websocketErrorHandleInterceptor(
				socket,
				(code) => this.handleSocketError(code)
			)
		);

		return socket;
	}

	private messageHandle(socket?: WebSocket) {
		if (!socket) return;

		fromEvent<MessageEvent>(socket, 'message')
			.pipe(
				skip(1),
				untilDestroyed(this),
				map((event) => event.data as string),
				map((oldMessage) => oldMessage.substring(0, oldMessage.length - 1)),
			)
			.subscribe((message) => {
				let response: IUsuarioSocketDefaultResponse;
				message.split('').forEach((splitedMessage) => {
					response = JSON.parse(splitedMessage || '{}');

					if (!this.isDefaultResponse(response)) return;

					response.arguments
						.map((argumentResponse) => ({
							...argumentResponse,
							value: this.parseArgumentValue(argumentResponse.value),
						}))
						.forEach((argument) => {
							const value = argument.value;
							if (value && value.Notificacao !== null) {
								if (this.messageHandleService.openedMessage && document.hasFocus()) {
									return;
								}

								if (!this.notificationService.checkNotificationSupport()) {
									this.notificationService.requestPermission();
								}

								const notification = value.Notificacao;
								this.notificationService.showNotification(notification.Titulo, {
									icon: ImageBase64Formater.formater(notification.Imagem) ?? undefined,
									body: notification.Texto,
								});
							}
						});
				});
			});
	}

	tryReconnect(): Observable<boolean> {
		return from(this.startDomain());
	}

	private parseArgumentValue(value: string | IUsuarioSocketResponse) {
		return typeof value == 'string' ? (JSON.parse(value) as IUsuarioSocketResponse) : value;
	}

	private isDefaultResponse(data: TUsuarioSocketResponse): data is IUsuarioSocketDefaultResponse {
		return (data as IUsuarioSocketDefaultResponse)?.target == 'receivemessage';
	}

	private handleSocketError(code: number) {
		const userApiService = this.injector.get(UserApiService);
		const user = userApiService.currentUser$$.value;

		if (user) {
			const payload: IWebSocketLogError = {
				seqCliente: user.seq_cliente,
				usuarioId: user.id,
				statusCode: code,
			};

			userApiService.webSocketLog(payload).subscribe();
		}
	}
}
